import { View } from "react-native";
import React, { useCallback, useMemo } from "react";
import { AccountMemberDetails } from "features/types";
import ReservationDetails from "./ReservationDetails";
import { useAccount } from "hooks/useAccount";
import WhiteText from "components/WhiteText";
import useCurrentUser from "hooks/useCurrentUser";
import Spacer from "components/Spacer";
import i18n from "plugins/localization";
import BankDetails from "./BankDetails";
import VoucherDetails from "./VoucherDetails";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";

const MemberSubscriptionSummary = ({
  member,
}: {
  member: AccountMemberDetails;
}) => {
  const { account } = useAccount();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const getAccountReservationSubs = (member: AccountMemberDetails) => {
    return account!.subscriptions.filter(
      (sub) =>
        getPlanType(sub.planPackage) === "RESERVATION" &&
        sub.member &&
        sub.member.id === member.id &&
        filterIneligibleSubscriptions(sub) &&
        sub.child === false
    );
  };

  const accountReservationSubs = getAccountReservationSubs(member);
  const accountVoucherSubs = useMemo(() => {
    if (!account) return [];
    return account.subscriptions.filter(
      (sub) =>
        getPlanType(sub.planPackage) === "VOUCHER" &&
        filterIneligibleSubscriptions(sub)
    );
  }, [account]);

  const accountBankSubscription = useMemo(() => {
    if (!account) return undefined;
    return account.subscriptions.find(
      (sub) =>
        getPlanType(sub.planPackage) === "BANK" &&
        filterIneligibleSubscriptions(sub) &&
        sub.child === false
    );
  }, [account]);

  const bankBalance = useMemo(() => {
    if (!tenantConfig || !account) return 0;
    const accountBankBalance = account.balances.find((balance) =>
      balance.code.includes("BANK")
    );
    return accountBankBalance?.balance || 0;
  }, [account, tenantConfig]);

  if (!account) return null;

  return (
    <View>
      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <WhiteText category="h5">
          {i18n.t("screens.Dashboard.myWallet")}
        </WhiteText>
      </View>
      <Spacer />

      {member && accountReservationSubs.length > 0 && (
        <ReservationDetails memberReservationSubs={accountReservationSubs} />
      )}

      {accountVoucherSubs.length > 0 && (
        <VoucherDetails accountVoucherSubs={accountVoucherSubs} />
      )}

      {accountBankSubscription !== undefined && (
        <BankDetails
          accountSubscriptions={account.subscriptions}
          accountBankSubscription={accountBankSubscription}
          bankBalance={bankBalance}
        />
      )}
    </View>
  );
};

export default MemberSubscriptionSummary;
